import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { useNavigate } from 'react-router-dom';

/*
 * Not found — in the same state-card family as the analytics empty states
 * (centred card, soft wash, gradient tile), not the old 8rem red "404" with a
 * salmon button. The button also said "Go Back To Domains" and went to the
 * dashboard; it now says where it goes, and Back is offered for a mistyped link.
 */
const GRAD = 'linear-gradient(135deg,#2a78d6,#7c3aed)';

const NotFound = () => {
  const navigate = useNavigate();
  const canGoBack = typeof window !== 'undefined' && window.history.length > 1;

  return (
    <Box sx={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2, py: 6 }}>
      <Box
        sx={{
          position: 'relative', width: '100%', maxWidth: 480, textAlign: 'center', overflow: 'hidden',
          px: { xs: 3, md: 5.5 }, py: { xs: 4.5, md: 5.5 }, borderRadius: 4,
          bgcolor: 'var(--surface)', border: '1px solid var(--line)',
          boxShadow: '0 24px 54px -26px rgba(15,23,42,0.22), 0 2px 8px rgba(15,23,42,0.04)',
        }}
      >
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 140, pointerEvents: 'none', background: 'radial-gradient(120% 100% at 50% 0%, rgba(124,58,237,0.14), rgba(42,120,214,0.07) 46%, transparent 76%)' }} />
        <Box sx={{ position: 'relative' }}>
          <Box sx={{ width: 68, height: 68, mx: 'auto', mb: 2.5, borderRadius: '22px', display: 'grid', placeItems: 'center', background: GRAD, color: '#fff', boxShadow: '0 16px 32px -10px rgba(124,58,237,0.55)' }}>
            <TravelExploreRoundedIcon sx={{ fontSize: 34 }} />
          </Box>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.12em', color: 'var(--ink-4)', mb: 0.75 }}>
            ERROR 404
          </Typography>
          <Typography component="h1" sx={{ fontSize: { xs: '1.15rem', sm: '1.3rem' }, fontWeight: 800, color: 'var(--ink)', mb: 1, letterSpacing: '-0.01em' }}>
            We couldn’t find that page
          </Typography>
          <Typography sx={{ fontSize: '0.92rem', color: 'var(--ink-3)', lineHeight: 1.6, mx: 'auto', maxWidth: '40ch' }}>
            The link may be mistyped, or the page has moved or been removed.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} justifyContent="center" alignItems="center" sx={{ mt: 3.25 }}>
            <Button
              variant="contained"
              disableElevation
              onClick={() => navigate('/dashboard/statistics')}
              sx={{
                textTransform: 'none', fontWeight: 700, borderRadius: 2.5, px: 3, py: 0.9, color: '#fff', background: GRAD,
                boxShadow: '0 10px 22px -10px rgba(124,58,237,0.6)',
                '&:hover': { background: 'linear-gradient(135deg,#1f63b8,#6a2bd6)' },
              }}
            >
              Go to dashboard
            </Button>
            {canGoBack && (
              <Button
                startIcon={<ArrowBackRoundedIcon />}
                onClick={() => navigate(-1)}
                sx={{ textTransform: 'none', fontWeight: 600, color: 'var(--ink-3)', '&:hover': { color: 'var(--ink)', bgcolor: 'transparent' } }}
              >
                Go back
              </Button>
            )}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default NotFound;
